// What KIND of glTF is this? — the pure half.
//
// Three different assets in this app arrive as `.glb`, and the extension
// cannot separate them:
//
//   - a BODY ANIMATION (the `body-anim` wizard, which already owns `.glb`),
//   - a CHARACTER RIG (the rig wizard),
//   - a GARMENT (ENG-1108).
//
// Registering a second `.glb` claimant makes `candidatesFor` return two keys,
// which `buildUploadQueue` maps to `ambiguous-extension` — and CLAUDE.md
// records what that used to do for `.json`: a key with no registered component,
// warned about once and silently advanced past, DISCARDING the file. So the
// bytes decide, exactly as they already do for a blueprint export. A rename
// cannot change what a file is, in either direction.
//
// The signatures are MEASURED, not guessed, on one real example of each
// (myra's `idle_balanced`, Sara's `isaac_tshirtJoggers_multiObject_thin_v01`,
// and `SK_Isaac_Body.fbx` converted to glTF):
//
//   | asset           | meshes | animations | JOINTS_0 |
//   |-----------------|--------|------------|----------|
//   | body animation  |      0 |          1 | —        |
//   | garment         |     36 |          0 | no       |
//   | character rig   |      1 |          1 | yes      |
//
// An animation GLB carries no geometry at all, and a rig is skinned where a
// garment is not, so the three do not overlap.
//
// No three.js import here, so node:test drives the rules.

export const GLTF_KIND = Object.freeze({
  animation: 'animation',
  rig: 'rig',
  garment: 'garment',
  unknown: 'unknown',
})

/** A JSON chunk larger than this is not sniffed — see `readGlbJsonChunk`. */
export const MAX_JSON_CHUNK = 8 * 1024 * 1024

const GLB_MAGIC = 0x46546c67   // 'glTF'
const CHUNK_JSON = 0x4e4f534a  // 'JSON'

/**
 * The parsed JSON chunk of a GLB, or of a plain `.gltf`.
 *
 * Only the FIRST chunk is read, and its length comes from the file's own
 * header, so a 7.7 MB garment costs a few tens of kilobytes to classify rather
 * than a full download.
 *
 * @param {ArrayBuffer|Uint8Array} head  at least the first 20 bytes; ideally the
 *   first chunk too. Returns null when it is not a GLB or the chunk is truncated.
 */
export function readGlbJsonChunk(head) {
  const bytes = head instanceof Uint8Array ? head : new Uint8Array(head)
  if (bytes.byteLength < 20) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint32(0, true) !== GLB_MAGIC) return null
  const chunkLength = view.getUint32(12, true)
  if (view.getUint32(16, true) !== CHUNK_JSON) return null
  if (chunkLength > MAX_JSON_CHUNK) return null
  if (bytes.byteLength < 20 + chunkLength) return null
  const text = new TextDecoder().decode(bytes.subarray(20, 20 + chunkLength))
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** How many bytes of a GLB must be read to classify it, from its header. */
export function jsonChunkBytesNeeded(head) {
  const bytes = head instanceof Uint8Array ? head : new Uint8Array(head)
  if (bytes.byteLength < 16) return 20
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint32(0, true) !== GLB_MAGIC) return 0
  return 20 + view.getUint32(12, true)
}

/**
 * Classify a parsed glTF document.
 *
 * Deliberately conservative: anything that does not match one of the three
 * signatures is `unknown`, which the caller must turn into a QUESTION rather
 * than a guess. Silently filing a file under the wrong kind is worse than
 * asking, because the user is never told it happened.
 *
 * @param {object|null} gltf  the parsed JSON chunk
 * @returns {{ kind: string, reason: string }}
 */
export function classifyGltf(gltf) {
  if (!gltf || typeof gltf !== 'object') {
    return { kind: GLTF_KIND.unknown, reason: 'not a readable glTF document' }
  }
  const meshes = Array.isArray(gltf.meshes) ? gltf.meshes : []
  const animations = Array.isArray(gltf.animations) ? gltf.animations : []

  // Geometry is the first fork. An animation export carries tracks and a
  // skeleton and NO meshes — measured on every clip in myra's library.
  if (meshes.length === 0) {
    return animations.length > 0
      ? { kind: GLTF_KIND.animation, reason: `${animations.length} animation(s) and no mesh` }
      : { kind: GLTF_KIND.unknown, reason: 'no meshes and no animations — nothing to import' }
  }

  // Skinning is the second. A rig's meshes are bound to its skeleton; a draped
  // garment has no skin at all, which is why the importer has to transfer one.
  const skinned = meshes.some((m) =>
    Array.isArray(m.primitives) && m.primitives.some((p) => p?.attributes?.JOINTS_0 !== undefined))
  if (skinned) {
    return { kind: GLTF_KIND.rig, reason: `${meshes.length} mesh(es) skinned to a skeleton` }
  }

  const withPositions = meshes.filter((m) =>
    Array.isArray(m.primitives) && m.primitives.some((p) => p?.attributes?.POSITION !== undefined))
  if (withPositions.length === 0) {
    return { kind: GLTF_KIND.unknown, reason: 'meshes carry no POSITION' }
  }
  // The reason must state what was actually MEASURED. It used to say "and no
  // animation" while the branch never looked at `animations` — an unskinned
  // mesh carrying transform tracks (a prop) would be called a garment and the
  // sentence explaining it would be false. It is still SUGGESTED as a garment,
  // because unskinned geometry is what a garment looks like, but the suggestion
  // now says what it saw and the importer asks (gltfImportChoice.js).
  const anim = animations.length > 0 ? `, ${animations.length} animation(s)` : ', no animation'
  return {
    kind: GLTF_KIND.garment,
    reason: `${meshes.length} unskinned mesh(es) with geometry${anim}`,
  }
}
